import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { foldStartsOpen, rememberFold } from '../../components/ui/filterFold';
import { TableTools } from '../../components/ui/TableTools';
import { FilterIcon } from '../../components/ui/icons';
import { num } from '../../format';
import { ColumnFilter, isFiltering, type ColumnFilterValue } from './ColumnFilter';
import {
  getType,
  listPackages,
  listTypes,
  searchComponents,
  searchXlsx,
  type Attribute,
  type Component,
  type SearchParams,
} from '../components/api';
import { useUnitName } from '../components/useUnitName';
import { downloadBlob } from '../labels/api';
import { useNavigation } from '../layout/navigation';

/** Columns every part has, whatever it is. */
const FIXED = ['code', 'name', 'mpn', 'manufacturer', 'package', 'value', 'qty', 'reserved', 'free'] as const;
type FixedColumn = (typeof FIXED)[number];

const STORAGE_KEY = 'storageapp.stock.columns';
const FILTERS_KEY = 'storageapp.stock.filters';

/** Enough that a type's whole shelf is usually on screen at once, and low
 *  enough that the browser is not asked to lay out a thousand rows. The server
 *  refuses more than this anyway. */
const LIMIT = 200;

function readHidden(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

/** A parameter's value as one short string. */
function show(value: unknown, spec: Attribute | undefined, locale: string): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? '✓' : '—';
  if (spec?.enum_values) {
    const option = spec.enum_values.find((entry) => entry.value === String(value));
    if (option) return locale === 'en' ? option.label_en : option.label_ru;
  }
  return String(value);
}

/**
 * What is on the shelf, as a table.
 *
 * The catalogue screen answers "where is the part I am thinking of" one card at
 * a time. This answers a different question — "what have I got, and how much of
 * it" — and that one is only readable as rows and columns, compared down the
 * page rather than opened one by one.
 *
 * Which columns those are depends on the type: a table of resistors wants
 * resistance, tolerance and power, and none of those words mean anything on a
 * table of connectors. So choosing a type rewrites the header.
 *
 * Sorting and the per-column boxes work on what has been fetched, not on the
 * catalogue. That is a deliberate limit and it is stated on screen when it
 * bites: ordering a page of two hundred by a column and calling it "the
 * cheapest" would be a lie, and the alternative — ordering inside a jsonb
 * document on the server — is a different piece of work.
 */
export function StockTable() {
  const { t, i18n } = useTranslation();
  const { open } = useNavigation();
  const unitName = useUnitName();

  const [query, setQuery] = useState('');
  const [typeId, setTypeId] = useState('');
  const [packageId, setPackageId] = useState('');
  const [onlyStocked, setOnlyStocked] = useState(false);
  const [hidden, setHidden] = useState<Set<string>>(readHidden);
  const [picking, setPicking] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [descending, setDescending] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, ColumnFilterValue>>({});
  const [openFilter, setOpenFilter] = useState<{ key: string; box: DOMRect } | null>(null);
  const [showFilters, setShowFilters] = useState(() => foldStartsOpen(FILTERS_KEY));

  const types = useQuery({
    queryKey: ['reference', 'types', 'used'],
    queryFn: () => listTypes('used'),
  });
  const packages = useQuery({
    queryKey: ['reference', 'packages', 'used'],
    queryFn: () => listPackages('used'),
  });

  // The parameters of the chosen type. Without a type there is nothing to
  // offer: "±1%" is a tolerance on a resistor and nothing at all on a connector.
  const typeDetail = useQuery({
    queryKey: ['reference', 'type', typeId],
    queryFn: () => getType(typeId),
    enabled: Boolean(typeId),
  });

  // Whether "4k7" can be read at all: a value means nothing until the type says
  // ohms or farads, and the server refuses to guess.
  const valueKind = types.data?.find((type) => type.id === typeId)?.value_kind ?? null;

  const valueFilter = valueKind ? columnFilters.value : undefined;

  const params: SearchParams = {
    q: query || undefined,
    component_type_id: typeId || null,
    package_id: packageId || null,
    in_stock: onlyStocked,
    // The value range goes to the server rather than being applied here: it is
    // the server that knows "4k7" is 4700 ohms, and the same parser has to read
    // the range as read the stored value. It also means the range narrows the
    // whole catalogue instead of the page already fetched.
    value_min: valueFilter?.min?.trim() || undefined,
    value_max: valueFilter?.max?.trim() || undefined,
    sort: query ? 'relevance' : 'name',
    limit: LIMIT,
  };

  const rows = useQuery({
    queryKey: ['components', 'search', params],
    queryFn: () => searchComponents(params),
  });

  const attributes = useMemo(
    () => (typeDetail.data?.attributes ?? []).slice().sort((a, b) => a.sort_order - b.sort_order),
    [typeDetail.data],
  );

  const locale = i18n.resolvedLanguage ?? 'ru';

  /** One column's value for one row, as the string the cell shows — so that
   *  filtering and sorting agree with what is on screen. */
  const cell = useMemo(() => {
    const specs = new Map(attributes.map((spec) => [spec.key, spec]));
    return (component: Component, key: string): string => {
      switch (key as FixedColumn) {
        case 'code':
          return component.internal_code;
        case 'name':
          return component.name ?? '';
        case 'mpn':
          return component.mpn ?? '';
        case 'manufacturer':
          return component.manufacturer_name ?? '';
        case 'package':
          return component.package_code ?? '';
        case 'value':
          return component.value_display ?? '';
        case 'qty':
          return String(component.qty_on_hand ?? '');
        case 'reserved':
          return String(component.reserved_qty ?? '');
        case 'free':
          return String(component.free_qty ?? '');
        default:
          return show(component.attributes?.[key], specs.get(key), locale);
      }
    };
  }, [attributes, locale]);

  /** Which columns take a range rather than a word.
   *
   *  Value is one of them once a type has been chosen, and only then: without a
   *  type "100n" is farads or henries or nothing at all, and neither the server
   *  nor this screen is willing to guess. */
  const numeric = useMemo(() => {
    const specs = new Map(attributes.map((spec) => [spec.key, spec]));
    return (key: string) =>
      key === 'qty' || key === 'reserved' || key === 'free' ||
      (key === 'value' && Boolean(valueKind)) ||
      specs.get(key)?.data_type === 'number';
  }, [attributes, valueKind]);

  /** The number a cell sorts and filters by, or null when it holds no number. */
  const amount = useMemo(() => {
    return (component: Component, key: string): number | null => {
      const raw =
        key === 'qty' || key === 'reserved' || key === 'free'
          ? component[key === 'qty' ? 'qty_on_hand' : key === 'reserved' ? 'reserved_qty' : 'free_qty']
          : key === 'value'
            ? component.value_base
            : component.attributes?.[key];
      if (raw === null || raw === undefined || raw === '') return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    };
  }, []);

  /** Rows left after every filter except one column's own — which is what that
   *  column's own form has to measure, or opening it would narrow the range it
   *  is about to offer. */
  const filteredExcept = useMemo(() => {
    const all = rows.data?.map((hit) => hit.component) ?? [];
    return (skip: string | null) => {
      const wanted = Object.entries(columnFilters).filter(
        // Value is already narrowed by the server, so applying it again here
        // would compare "4k7" as text against "4.7k" and drop every row.
        ([key, value]) => key !== skip && key !== 'value' && isFiltering(value),
      );
      if (wanted.length === 0) return all;
      return all.filter((component) =>
        wanted.every(([key, filter]) => {
          if (numeric(key)) {
            const value = amount(component, key);
            if (value === null) return false;
            const from = Number(filter.min);
            const to = Number(filter.max);
            if (filter.min?.trim() && Number.isFinite(from) && value < from) return false;
            if (filter.max?.trim() && Number.isFinite(to) && value > to) return false;
            return true;
          }
          const needle = filter.text?.trim().toLocaleLowerCase() ?? '';
          return cell(component, key).toLocaleLowerCase().includes(needle);
        }),
      );
    };
  }, [rows.data, columnFilters, cell, numeric, amount]);

  const visible = useMemo(() => {
    let list = filteredExcept(null);

    if (sortBy) {
      const key = sortBy;
      const isNumber = numeric(key) || key === 'value';
      list = [...list].sort((left, right) => {
        // An empty cell always sinks, whichever way the column is pointing:
        // "nothing here" is not a small value, it is the absence of one.
        if (isNumber) {
          const a = amount(left, key);
          const b = amount(right, key);
          if (a === null && b === null) return 0;
          if (a === null) return 1;
          if (b === null) return -1;
          return descending ? b - a : a - b;
        }
        const a = cell(left, key);
        const b = cell(right, key);
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        const order = a.localeCompare(b, locale, { numeric: true });
        return descending ? -order : order;
      });
    }

    return list;
  }, [filteredExcept, sortBy, descending, cell, numeric, amount, locale]);

  /** The span a numeric column covers right now, written the way the column
   *  writes it — "4.7k" for a resistance, a plain number for a count. */
  const boundsOf = (key: string): { min: string; max: string } | null => {
    if (!numeric(key)) return null;
    const rows = filteredExcept(key)
      .map((component) => ({ component, value: amount(component, key) }))
      .filter((row): row is { component: Component; value: number } => row.value !== null);
    if (rows.length === 0) return null;

    let low = rows[0]!;
    let high = rows[0]!;
    for (const row of rows) {
      if (row.value < low.value) low = row;
      if (row.value > high.value) high = row;
    }

    // A resistance is offered back as "4.7k", not as 4700: that is what the
    // column shows, and what the server will read if it is applied unchanged.
    if (key === 'value') {
      return {
        min: low.component.value_display ?? String(low.value),
        max: high.component.value_display ?? String(high.value),
      };
    }
    return { min: num(String(low.value)), max: num(String(high.value)) };
  };

  const toggle = (key: string) => {
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        // The choice holds for this session; it just will not be remembered.
      }
      return next;
    });
  };

  const sortOn = (key: string) => {
    if (sortBy === key) {
      setDescending((was) => !was);
      return;
    }
    setSortBy(key);
    setDescending(false);
  };

  const shown = (key: string) => !hidden.has(key);
  const label = (spec: Attribute) =>
    (locale === 'en' ? spec.label_en : spec.label_ru) + (spec.unit ? `, ${spec.unit}` : '');

  const columns: { key: string; title: string; right?: boolean }[] = [
    ...FIXED.filter(shown).map((key) => ({
      key,
      title: t(`stock.table.column.${key}`),
      right: key === 'qty' || key === 'reserved' || key === 'free',
    })),
  ];
  // Parameters sit between the fixed description and the quantity, which is the
  // figure the eye lands on last.
  const withAttributes = [
    ...columns.filter((column) => column.key !== 'qty'),
    ...attributes
      .filter((spec) => shown(spec.key))
      .map((spec) => ({ key: spec.key, title: label(spec), right: false })),
    ...columns.filter((column) => column.key === 'qty'),
  ];

  const capped = (rows.data?.length ?? 0) >= LIMIT;
  const filtered = Object.values(columnFilters).some(isFiltering);

  /* How much is narrowing the list right now. Shown on the shut fold, because a
     block that hides four conditions without saying so is how somebody spends a
     minute wondering where half the catalogue went. */
  const narrowing =
    (query.trim() ? 1 : 0) +
    (typeId ? 1 : 0) +
    (packageId ? 1 : 0) +
    (onlyStocked ? 1 : 0) +
    Object.values(columnFilters).filter(isFiltering).length;

  return (
    <section className="stock-table">
      {/* One row that is always there: what is being filtered, and the two
          things one does with the table as it stands. The block below is what
          takes half a phone's screen, so it folds away. */}
      <div className="stock-table__bar">
        <button
          type="button"
          className="btn stock-table__toggle"
          aria-expanded={showFilters}
          onClick={() => {
            const next = !showFilters;
            setShowFilters(next);
            rememberFold(FILTERS_KEY, next);
          }}
        >
          <FilterIcon />
          {t('common.filters')}
          {narrowing > 0 && <span className="tabs__count">{narrowing}</span>}
        </button>
        <TableTools
          label={t('stock.tab.table')}
          onExport={() =>
            void searchXlsx(params).then((blob) => downloadBlob(blob, 'stock.xlsx'))
          }
        />
      </div>

      <div className="stock-table__filters" hidden={!showFilters}>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('components.searchHint')}
          aria-label={t('common.search')}
        />
        <select
          value={typeId}
          onChange={(event) => {
            setTypeId(event.target.value);
            // A range of resistances means nothing on capacitors, and the
            // server would read the same words as farads.
            setColumnFilters((current) => {
              const next = { ...current };
              delete next.value;
              return next;
            });
          }}
        >
          <option value="">{t('components.anyType')}</option>
          {(types.data ?? []).map((type) => (
            <option key={type.id} value={type.id}>
              {locale === 'en' ? type.name_en : type.name_ru}
            </option>
          ))}
        </select>
        <select value={packageId} onChange={(event) => setPackageId(event.target.value)}>
          <option value="">{t('components.anyPackage')}</option>
          {(packages.data ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.code}
            </option>
          ))}
        </select>
        <label className="components__instock">
          <input
            type="checkbox"
            checked={onlyStocked}
            onChange={(event) => setOnlyStocked(event.target.checked)}
          />
          {t('components.inStock')}
        </label>
        <button type="button" className="btn" onClick={() => setPicking((was) => !was)}>
          {t('stock.table.columns')}
        </button>
        {(filtered || sortBy) && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setColumnFilters({});
              setSortBy(null);
            }}
          >
            {t('stock.table.reset')}
          </button>
        )}
      </div>

      {picking && (
        <div className="stock-table__columns">
          {FIXED.map((key) => (
            <label key={key}>
              <input type="checkbox" checked={shown(key)} onChange={() => toggle(key)} />
              {t(`stock.table.column.${key}`)}
            </label>
          ))}
          {attributes.map((spec) => (
            <label key={spec.key}>
              <input type="checkbox" checked={shown(spec.key)} onChange={() => toggle(spec.key)} />
              {label(spec)}
            </label>
          ))}
        </div>
      )}

      {rows.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
      {/* A range the server could not read has to say so: an empty table would
          look like "there are none", which is a different answer. */}
      {rows.isError && (
        <p className="panel__error">
          {t('stock.table.badRange', {
            reason: rows.error instanceof ApiError ? rows.error.code : 'unknown',
          })}
        </p>
      )}
      {rows.data?.length === 0 && <p className="panel__hint">{t('components.empty')}</p>}
      {rows.data && rows.data.length > 0 && visible.length === 0 && (
        <p className="panel__hint">{t('stock.table.noneMatch')}</p>
      )}
      {capped && <p className="hint">{t('stock.table.capped', { n: LIMIT })}</p>}

      {/* Its own scroll box: a table of a dozen columns must not make the page
          itself slide sideways, and its headings stay put while the rows move —
          by the tenth row nobody remembers which column is which. */}
      <div className="scroll-x scroll-y">
        <table className="stock-table__grid">
          <thead>
            <tr>
              {withAttributes.map((column) => (
                <th key={column.key} className={column.right ? 'stock-table__qty' : undefined}>
                  <span className="stock-table__head">
                    <button
                      type="button"
                      className="stock-table__sort"
                      onClick={() => sortOn(column.key)}
                      aria-label={t('stock.table.sortBy', { column: column.title })}
                    >
                      {column.title}
                      {sortBy === column.key && (
                        <span aria-hidden="true">{descending ? ' ↓' : ' ↑'}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={
                        isFiltering(columnFilters[column.key])
                          ? 'stock-table__funnel is-active'
                          : 'stock-table__funnel'
                      }
                      aria-label={t('stock.table.filterBy', { column: column.title })}
                      onClick={(event) => {
                        // Measured here and not inside the updater below.
                        // React clears `currentTarget` the moment the handler
                        // returns, and an updater function runs later — reading
                        // it there threw on null and took the whole screen with
                        // it.
                        const box = event.currentTarget.getBoundingClientRect();
                        setOpenFilter((current) =>
                          current?.key === column.key ? null : { key: column.key, box },
                        );
                      }}
                    >
                      <FilterIcon />
                    </button>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((component) => {
              const low =
                component.min_stock !== null &&
                Number(component.qty_on_hand) < Number(component.min_stock);
              return (
                <tr key={component.id} onClick={() => open('component', component.id)}>
                  {withAttributes.map((column) => {
                    if (column.key === 'code') {
                      return (
                        <td key={column.key} className="stock-table__code">
                          <span className="components__code">{component.internal_code}</span>
                        </td>
                      );
                    }
                    if (column.key === 'name') {
                      return (
                        <td key={column.key} className="stock-table__name">
                          {component.name}
                        </td>
                      );
                    }
                    if (column.key === 'qty' || column.key === 'reserved' || column.key === 'free') {
                      return (
                        <td key={column.key} className="stock-table__qty">
                          <strong className={column.key === 'qty' && low ? 'stock__negative' : undefined}>
                            {num(
                              column.key === 'qty'
                                ? component.qty_on_hand
                                : column.key === 'reserved'
                                  ? component.reserved_qty
                                  : component.free_qty,
                            )}
                          </strong>
                          <em className="stock__when"> {unitName(component.unit_code)}</em>
                        </td>
                      );
                    }
                    return <td key={column.key}>{cell(component, column.key)}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openFilter && (
        <ColumnFilter
          // Remounted per column, so the fields open holding that column's own
          // range rather than the one before it.
          key={openFilter.key}
          title={
            withAttributes.find((column) => column.key === openFilter.key)?.title ?? openFilter.key
          }
          numeric={numeric(openFilter.key)}
          box={openFilter.box}
          value={columnFilters[openFilter.key]}
          bounds={boundsOf(openFilter.key)}
          hint={openFilter.key === 'value' ? t('stock.table.valueHint') : undefined}
          onApply={(value) => {
            setColumnFilters((current) => ({ ...current, [openFilter.key]: value }));
            setOpenFilter(null);
          }}
          onClear={() => {
            setColumnFilters((current) => {
              const next = { ...current };
              delete next[openFilter.key];
              return next;
            });
            setOpenFilter(null);
          }}
          onClose={() => setOpenFilter(null)}
        />
      )}
    </section>
  );
}
